import * as THREE from 'three';
import { Tank } from './Tank';
import { AIController, AITactic } from '../systems/AIController';

export class Enemy extends Tank {
  private target: THREE.Vector3 | null = null;
  private patrolPoints: THREE.Vector3[] = [];
  private detectionRange: number = 30;
  private shootRange: number = 25;
  private patrolSpeed: number = 0.3;
  private chaseSpeed: number = 0.7;
  private state: 'patrol' | 'chase' | 'attack' = 'patrol';
  private stateTimer: number = 0;
  private wanderTimer: number = 0;
  private wanderInterval: number = 3; // 每 3 秒随机改变方向

  // AI 控制器
  protected aiController: AIController;
  protected useAdvancedAI: boolean = true; // 是否使用高级AI

  constructor(x: number, z: number, tactic: AITactic = AITactic.BALANCED) {
    super(x, z, 0xff0000); // 红色敌人

    // 创建AI控制器
    this.aiController = new AIController(tactic);

    // 生成巡逻点
    this.generatePatrolPoints(x, z);

    // 添加用户数据
    this.body.userData = { type: 'enemy', tank: this };
  }

  private generatePatrolPoints(centerX: number, centerZ: number): void {
    const radius = 15;
    const numPoints = 4;

    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      const x = centerX + Math.cos(angle) * radius;
      const z = centerZ + Math.sin(angle) * radius;
      this.patrolPoints.push(new THREE.Vector3(x, 0, z));
    }
  }

  updateAI(playerPosition: THREE.Vector3, deltaTime: number): void {
    if (!this.isAlive) return;

    if (this.useAdvancedAI) {
      // 使用高级AI
      const decision = this.aiController.update(
        deltaTime,
        this.mesh.position,
        playerPosition,
        this.health,
        this.maxHealth
      );

      // 执行AI决策
      if (decision.forward !== 0 || decision.turn !== 0) {
        this.move(decision.forward, decision.turn, deltaTime);
      }

      // 面向玩家（用于射击）
      this.aimAt(playerPosition);

      // 设置是否应该射击
      if (decision.shouldShoot && this.canShoot) {
        this.stateTimer = 2; // 标记为可以射击状态
      }
    } else {
      // 使用原始简单AI
      this.stateTimer += deltaTime;
      this.wanderTimer += deltaTime;

      const distanceToPlayer = this.mesh.position.distanceTo(playerPosition);

      // 状态机
      if (distanceToPlayer <= this.shootRange) {
        this.state = 'attack';
      } else if (distanceToPlayer <= this.detectionRange) {
        this.state = 'chase';
      } else {
        this.state = 'patrol';
      }

      // 根据状态执行行为
      switch (this.state) {
        case 'patrol':
          this.patrol(deltaTime);
          break;
        case 'chase':
          this.chase(playerPosition, deltaTime);
          break;
        case 'attack':
          this.attack(playerPosition, deltaTime);
          break;
      }
    }
  }

  private patrol(deltaTime: number): void {
    // 简单的随机游走
    if (this.wanderTimer >= this.wanderInterval) {
      this.wanderTimer = 0;
      // 随机选择一个方向
      const randomAngle = Math.random() * Math.PI * 2;
      const randomDistance = 5 + Math.random() * 10;
      this.target = new THREE.Vector3(
        this.mesh.position.x + Math.cos(randomAngle) * randomDistance,
        0,
        this.mesh.position.z + Math.sin(randomAngle) * randomDistance
      );
    }

    if (this.target) {
      this.moveTowards(this.target, this.patrolSpeed, deltaTime);
    }
  }

  private chase(playerPosition: THREE.Vector3, deltaTime: number): void {
    this.moveTowards(playerPosition, this.chaseSpeed, deltaTime);
  }

  private attack(playerPosition: THREE.Vector3, deltaTime: number): void {
    // 瞄准玩家
    this.aimAt(playerPosition);

    // 尝试射击（由主游戏循环调用实际射击）
    if (this.canShoot && this.stateTimer > 1) {
      this.shoot();
      this.stateTimer = 0;
    }

    // 保持一定距离
    const distance = this.mesh.position.distanceTo(playerPosition);
    if (distance < this.shootRange * 0.7) {
      // 太近了，后退
      const awayDirection = new THREE.Vector3()
        .subVectors(this.mesh.position, playerPosition)
        .normalize();
      const awayPoint = this.mesh.position.clone().add(awayDirection.multiplyScalar(5));
      this.moveTowards(awayPoint, 0.3, deltaTime);
    } else if (distance > this.shootRange * 0.9) {
      // 太远了，靠近
      this.moveTowards(playerPosition, 0.5, deltaTime);
    }
  }

  private moveTowards(target: THREE.Vector3, speed: number, deltaTime: number): void {
    const direction = new THREE.Vector3()
      .subVectors(target, this.mesh.position)
      .normalize();

    // 计算目标角度
    const targetAngle = Math.atan2(direction.x, direction.z);
    const currentAngle = this.currentRotation;

    // 平滑转向
    let angleDiff = targetAngle - currentAngle;
    // 标准化角度差
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    const turnAmount = Math.sign(angleDiff) * Math.min(Math.abs(angleDiff), 2 * deltaTime);
    this.move(speed, turnAmount / deltaTime, deltaTime);
  }

  private aimAt(target: THREE.Vector3): void {
    const direction = new THREE.Vector3()
      .subVectors(target, this.mesh.position)
      .normalize();

    const angle = Math.atan2(direction.x, direction.z);
    this.rotateTurret(angle - this.currentRotation);
  }

  shouldShoot(): boolean {
    if (this.useAdvancedAI) {
      return this.stateTimer > 1;
    }
    return this.state === 'attack' && this.canShoot;
  }

  /**
   * 设置AI的障碍物信息
   */
  setObstacles(obstacles: Array<{ position: THREE.Vector3; radius: number }>): void {
    this.aiController.setObstacles(obstacles);
  }

  /**
   * 设置是否使用高级AI
   */
  setAdvancedAI(enabled: boolean): void {
    this.useAdvancedAI = enabled;
  }

  /**
   * 获取AI状态（用于调试）
   */
  getAIState(): string {
    if (this.useAdvancedAI) {
      return this.aiController.getState();
    }
    return this.state;
  }

  reset(x: number, z: number): void {
    super.reset(x, z);
    this.state = 'patrol';
    this.stateTimer = 0;
    this.wanderTimer = 0;
    this.target = null;
    this.generatePatrolPoints(x, z);
  }

  // 可被子类重写
  getDamage(): number {
    return 20;
  }

  getScore(): number {
    return 100;
  }
}
